import {
  formatARS,
  STATS_GRANULARITIES,
  STATS_MAX_RANGE_DAYS,
  type StatsGranularity,
  type StatsOverviewDto,
  type StatsTotals,
} from '@gl/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, ChartNoAxesColumn, Minus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/FullScreenLoader';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errorMessage } from '@/lib/api';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { cn } from '@/lib/utils';
import { formatBusinessDate } from '../caja/format';
import { cajaKeys, stats } from '../caja/queries';
import { ChartStyles } from './chart-theme';
import { EmptyChart, ExpensesIncomeChart, PaymentMethodsChart, SalesLineChart } from './charts';
import {
  currentBusinessDate,
  GRANULARITY_LABELS,
  PRESET_LABELS,
  presetRange,
  rangeDays,
  type RangePreset,
} from './ranges';

const PRESETS: RangePreset[] = ['7d', '30d', 'month', 'lastMonth', 'custom'];

function defaultGranularity(days: number): StatsGranularity {
  if (days <= 45) return 'day';
  if (days <= 180) return 'week';
  return 'month';
}

/** Variación % con flecha + texto (no solo color). `inverse`: subir es malo (gastos). */
export function Delta({ value, inverse }: { value: number | null; inverse?: boolean }) {
  if (value === null) return <span className="text-xs text-muted-foreground">sin datos previos</span>;
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const good = value === 0 ? null : inverse ? value < 0 : value > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium tabular-nums',
        good === null ? 'text-muted-foreground' : good ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon className="size-3.5" />
      {value > 0 ? '+' : ''}
      {value.toLocaleString('es-AR')}%
    </span>
  );
}

const OVERVIEW_CARDS: { key: keyof StatsTotals; label: string; money: boolean; inverse?: boolean }[] = [
  { key: 'salesTotalCents', label: 'Ventas', money: true },
  { key: 'salesCount', label: 'Tickets', money: false },
  { key: 'avgTicketCents', label: 'Ticket promedio', money: true },
  { key: 'expensesTotalCents', label: 'Gastos', money: true, inverse: true },
  { key: 'incomesTotalCents', label: 'Ingresos', money: true },
  { key: 'resultCents', label: 'Resultado', money: true },
];

export function OverviewCards({ data, className }: { data: StatsOverviewDto | undefined; className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6', className)}>
      {OVERVIEW_CARDS.map((c) => {
        const v = data?.current[c.key];
        return (
          <div key={c.key} className="min-w-0 rounded-xl border bg-card p-3">
            <p className="truncate text-xs text-muted-foreground">{c.label}</p>
            <p className="truncate text-lg font-semibold tabular-nums">
              {v === undefined ? '—' : c.money ? formatARS(c.key === 'avgTicketCents' ? Math.round(v / 100) * 100 : v) : v.toLocaleString('es-AR')}
            </p>
            {data && <Delta value={data.deltaPct[c.key]} inverse={c.inverse} />}
          </div>
        );
      })}
    </div>
  );
}

function ChartCard({
  title,
  description,
  loading,
  error,
  children,
  className,
}: {
  title: string;
  description?: string;
  loading: boolean;
  error: unknown;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {loading ? (
          <EmptyChart>
            <Spinner className="size-5" />
          </EmptyChart>
        ) : error ? (
          <EmptyChart>
            <span className="text-destructive">{errorMessage(error)}</span>
          </EmptyChart>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

export function EstadisticasPage() {
  const local = useCurrentLocal();
  const allowed = useHasRole('OWNER', 'MANAGER');
  const today = useMemo(
    () => currentBusinessDate(local.timezone, local.businessDayStartHour),
    [local.timezone, local.businessDayStartHour],
  );
  const [preset, setPreset] = useState<RangePreset>('30d');
  const [custom, setCustom] = useState(() => presetRange('30d', today));
  const [granularity, setGranularity] = useState<StatsGranularity | 'auto'>('auto');

  const range = preset === 'custom' ? custom : presetRange(preset, today);
  const days = rangeDays(range.from, range.to);
  const rangeError =
    range.from > range.to
      ? 'La fecha "desde" tiene que ser anterior a "hasta".'
      : days > STATS_MAX_RANGE_DAYS
        ? `El rango no puede superar ${STATS_MAX_RANGE_DAYS} días.`
        : null;
  const g = granularity === 'auto' ? defaultGranularity(days) : granularity;
  const enabled = allowed && !rangeError;
  const key = (name: string, extra: object = {}) => [...cajaKeys.stats(local.id), name, range, extra] as const;
  const common = { enabled, placeholderData: keepPreviousData };

  const overview = useQuery({ queryKey: key('overview'), queryFn: () => stats.overview(local.id, range), ...common });
  const series = useQuery({
    queryKey: key('sales-series', { g }),
    queryFn: () => stats.salesSeries(local.id, { ...range, granularity: g }),
    ...common,
  });
  const evi = useQuery({
    queryKey: key('expenses-vs-income', { g }),
    queryFn: () => stats.expensesVsIncome(local.id, { ...range, granularity: g }),
    ...common,
  });
  const methods = useQuery({ queryKey: key('payment-methods'), queryFn: () => stats.paymentMethods(local.id, range), ...common });
  const top = useQuery({
    queryKey: key('top-items', { limit: 10 }),
    queryFn: () => stats.topItems(local.id, { ...range, limit: 10 }),
    ...common,
  });
  const margin = useQuery({ queryKey: key('margin'), queryFn: () => stats.margin(local.id, range), ...common });

  if (!allowed) {
    return (
      <div>
        <PageHeader title="Estadísticas" />
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Las estadísticas las ven el dueño y el encargado.
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasSales = (series.data?.points ?? []).some((p) => p.salesTotalCents > 0);
  const hasMovements = (evi.data?.points ?? []).some((p) => p.expensesTotalCents > 0 || p.incomesTotalCents > 0);
  const m = margin.data;
  // Rango sin ventas ni movimientos: un solo estado vacío en vez de seis gráficos vacíos.
  const rangeEmpty = !!series.data && !!evi.data && !hasSales && !hasMovements && !series.isFetching && !evi.isFetching;

  return (
    <div className="gl-viz">
      <ChartStyles />
      <PageHeader title="Estadísticas" description="Por día de caja. Las ventas anuladas no cuentan." />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 md:mx-0 md:px-0">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                if (p === 'custom' && preset !== 'custom') setCustom(range);
                setPreset(p);
              }}
              className={cn(
                'h-9 shrink-0 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors',
                preset === p ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-accent',
              )}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>
        <Select value={granularity} onValueChange={(v) => setGranularity(v as StatsGranularity | 'auto')}>
          <SelectTrigger className="h-9 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Automático</SelectItem>
            {STATS_GRANULARITIES.map((x) => (
              <SelectItem key={x} value={x}>
                {GRANULARITY_LABELS[x]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {preset === 'custom' && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            type="date"
            aria-label="Desde"
            className="h-9 w-40"
            value={custom.from}
            max={today}
            onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))}
          />
          <span className="text-sm text-muted-foreground">a</span>
          <Input
            type="date"
            aria-label="Hasta"
            className="h-9 w-40"
            value={custom.to}
            max={today}
            onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))}
          />
        </div>
      )}
      {rangeError && <p className="mb-4 text-sm text-destructive">{rangeError}</p>}

      <OverviewCards data={overview.data} className="mb-4" />
      {overview.data && (
        <p className="-mt-2 mb-4 text-xs text-muted-foreground">
          Comparado con {formatBusinessDate(overview.data.previousRange.from, { weekday: false, year: true })} a{' '}
          {formatBusinessDate(overview.data.previousRange.to, { weekday: false, year: true })}.
        </p>
      )}

      {rangeEmpty ? (
        <Card>
          <EmptyState
            icon={ChartNoAxesColumn}
            title="No hay datos en este rango"
            description={
              local.salesCount > 0
                ? 'No hubo ventas ni movimientos de caja en estas fechas. Probá con un rango más amplio.'
                : 'Cuando cargues ventas en la caja, acá vas a ver cuánto vendiste, qué sale más y tu margen.'
            }
            action={
              <>
                {local.salesCount > 0 && preset !== '30d' && (
                  <Button variant="outline" onClick={() => setPreset('30d')}>
                    Ver últimos 30 días
                  </Button>
                )}
                <Button asChild>
                  <Link to="../caja">Ir a la caja</Link>
                </Button>
              </>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Ventas" className="lg:col-span-2" loading={series.isLoading} error={series.error}>
          {hasSales && series.data ? <SalesLineChart points={series.data.points} granularity={g} /> : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Gastos vs ingresos" loading={evi.isLoading} error={evi.error}>
          {hasMovements && evi.data ? <ExpensesIncomeChart points={evi.data.points} granularity={g} /> : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Métodos de pago" loading={methods.isLoading} error={methods.error}>
          {methods.data && <PaymentMethodsChart methods={methods.data.methods} totalCents={methods.data.totalCents} />}
        </ChartCard>

        <ChartCard title="Lo más vendido" loading={top.isLoading} error={top.error}>
          {!top.data?.items.length ? (
            <EmptyChart />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="hidden w-8 sm:table-cell">#</TableHead>
                  <TableHead>Producto</TableHead>
                  <TableHead className="text-right">Cant.</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top.data.items.map((i, idx) => (
                  <TableRow key={`${i.itemId ?? 'free'}:${i.name}`}>
                    <TableCell className="hidden text-muted-foreground tabular-nums sm:table-cell">{idx + 1}</TableCell>
                    <TableCell className="max-w-32 truncate font-medium sm:max-w-48">{i.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{i.quantity.toLocaleString('es-AR')}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatARS(i.totalCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ChartCard>

        <ChartCard
          title="Margen"
          description="Calculado solo sobre los productos que tienen costo cargado."
          loading={margin.isLoading}
          error={margin.error}
        >
          {m && (
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Margen</p>
                  <p className="text-2xl font-semibold tabular-nums">{m.marginPct === null ? '—' : `${m.marginPct}%`}</p>
                  <p className="text-sm text-muted-foreground tabular-nums">{formatARS(m.marginCents)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Costo de lo vendido</p>
                  <p className="text-2xl font-semibold tabular-nums">{formatARS(m.costCents)}</p>
                  <p className="text-sm text-muted-foreground tabular-nums">sobre {formatARS(m.coveredRevenueCents)}</p>
                </div>
              </div>
              <div>
                <div className="mb-1 flex justify-between text-xs">
                  <span className="text-muted-foreground">Cobertura de costos</span>
                  <span className="font-medium tabular-nums">{m.coveragePct === null ? '—' : `${m.coveragePct}%`}</span>
                </div>
                <div
                  className="h-2 overflow-hidden rounded-full bg-muted"
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={m.coveragePct ?? 0}
                  aria-label="Cobertura de costos"
                >
                  <div className="h-full rounded-full" style={{ width: `${m.coveragePct ?? 0}%`, background: 'var(--viz-1)' }} />
                </div>
                {m.coveragePct !== null && m.coveragePct < 100 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {formatARS(m.revenueCents - m.coveredRevenueCents)} de lo facturado no tiene costo cargado. Cargalo en el
                    menú para que el margen sea más preciso.
                  </p>
                )}
              </div>
            </div>
          )}
        </ChartCard>
        </div>
      )}
    </div>
  );
}
